/**
 * PlaybookTab.jsx — Open House Planner field playbook (/openhouse?tab=playbook)
 *
 * The prospecting strategy as a polished, always-available reference, plus a
 * live scorecard computed from logged visits (oh_plan_stops), delivered
 * follow-ups, and open-house-tagged tracked-link clicks. Scripts are quoted
 * verbatim so the crew can read them straight off the phone in the field.
 */

import { useMemo } from 'react';
import {
  Box, Stack, Typography, Paper, Chip, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useAllOhPlanStops, useOhPlans } from '../../hooks/useOpenHouses';
import { useLinks } from '../../hooks/useLinks';
import { SCORECARD_TARGETS } from '../../lib/openHouseTemplates';

const VILLAGE_COLOR = '#E879F9';
const PT = 'America/Los_Angeles';

function fmtPlanDate(d) {
  if (!d) return '—';
  return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    timeZone: PT, weekday: 'short', month: 'short', day: 'numeric',
  });
}

// ── A quoted script line ────────────────────────────────────────────────
function Script({ children }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, my: 0.75, borderLeft: '3px solid', borderColor: 'primary.main', bgcolor: 'action.hover' }}>
      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>“{children}”</Typography>
    </Paper>
  );
}

function P({ children, sx }) {
  return <Typography variant="body2" color="text.secondary" sx={{ mb: 1, ...sx }}>{children}</Typography>;
}

function Section({ title, defaultExpanded, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function ScoreTile({ n, label, hint }) {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, textAlign: 'center', minWidth: 92, flex: '1 1 92px' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{n}</Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{label}</Typography>
      {hint && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{hint}</Typography>}
    </Paper>
  );
}

export default function PlaybookTab() {
  const { data: stops = [] } = useAllOhPlanStops();
  const { data: plans = [] } = useOhPlans();
  const { data: links = [] } = useLinks();

  // ── Live scorecard ──────────────────────────────────────────────────
  const score = useMemo(() => {
    const done = stops.filter((s) => s.status === 'done');
    const tally = (list) => ({
      visited: list.length,
      convos: list.filter((s) => s.outcome?.talked).length,
      shoots: list.filter((s) => s.outcome?.shootType && s.outcome.shootType !== 'none').length,
      onCam: list.filter((s) => s.outcome?.shootType === 'agent_on_camera').length,
      cells: list.filter((s) => s.outcome?.gotCell).length,
      delivered: list.filter((s) => s.outcome?.followUpSent).length,
    });
    const clicks = links
      .filter((l) => Array.isArray(l.tags) && l.tags.includes('open-house'))
      .reduce((sum, l) => sum + (l.click_count || 0), 0);

    // Per-outing (plan) breakdown, most recent first
    const stopsByPlan = {};
    for (const s of done) (stopsByPlan[s.planId] ||= []).push(s);
    const perOuting = plans
      .filter((p) => stopsByPlan[p.id]?.length)
      .map((p) => ({ plan: p, ...tally(stopsByPlan[p.id]) }))
      .sort((a, b) => String(b.plan.planDate).localeCompare(String(a.plan.planDate)));

    return { allTime: { ...tally(done), clicks }, perOuting };
  }, [stops, plans, links]);

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', pb: 6 }}>
      {/* Scorecard */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Scorecard</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <ScoreTile n={score.allTime.visited} label="Visited" />
          <ScoreTile n={score.allTime.convos} label="Conversations" />
          <ScoreTile n={score.allTime.shoots} label="Shoots" />
          <ScoreTile n={score.allTime.onCam} label="Agent on cam" />
          <ScoreTile n={score.allTime.cells} label="Cells" />
          <ScoreTile n={score.allTime.delivered} label="Delivered" />
          <ScoreTile n={score.allTime.clicks} label="Link views" />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          Targets per outing: {SCORECARD_TARGETS.housesPerDay} houses · {SCORECARD_TARGETS.convosPerDay} conversations · {SCORECARD_TARGETS.shootsPerDay} shoots · deliver within {SCORECARD_TARGETS.deliveryHours}h.
          Benchmark: ~1 real opportunity per 3 houses; first paid conversion typically within 1–2 weeks when follow-up cadence is maintained.
        </Typography>

        {score.perOuting.length > 0 && (
          <Box sx={{ overflowX: 'auto', mt: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Outing</TableCell>
                  <TableCell align="right">Visited</TableCell>
                  <TableCell align="right">Convos</TableCell>
                  <TableCell align="right">Shoots</TableCell>
                  <TableCell align="right">Cells</TableCell>
                  <TableCell align="right">Delivered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {score.perOuting.map((o) => (
                  <TableRow key={o.plan.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.plan.title || 'Outing'}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtPlanDate(o.plan.planDate)}</Typography>
                    </TableCell>
                    <TableCell align="right">{o.visited}</TableCell>
                    <TableCell align="right">{o.convos}</TableCell>
                    <TableCell align="right">{o.shoots}</TableCell>
                    <TableCell align="right">{o.cells}</TableCell>
                    <TableCell align="right">{o.delivered}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {/* Strategy */}
      <Section title="The play — brokerage of the day" defaultExpanded>
        <P>
          Pick <strong>one brokerage per outing</strong> and build the day around it.{' '}
          <Chip size="small" label="Village Properties" sx={{ bgcolor: VILLAGE_COLOR, color: '#fff', mx: 0.25 }} /> is target #1:
          180+ agents, three offices, ~16% of the SB luxury market, and a curated “trusted-vendor” list that one happy agent can get you onto.
        </P>
        <P>
          The pitch is a <strong>limited editorial series</strong> featuring that brokerage’s open houses — one free reel per agent, ever,
          delivered in under 24 hours, agent on camera. Weight the stops toward higher-price listings and agents who are active on Instagram
          (they’ll actually post the video). Three to six houses, one day, two people: <strong>Cyprian talks, Sierra shoots.</strong>
        </P>
      </Section>

      <Section title="Prep — Thursday / Friday (60–90 min)">
        <P>1. Refresh the scrape (Browse tab) → filter to the target brokerage / area → build the route in the Plan tab.</P>
        <P>2. Warm up 2–3 target agents on Instagram: follow, like a couple of posts, leave one genuine comment. Now you’re not a stranger.</P>
        <P>3. Optionally pre-clear <strong>one anchor house</strong> by DM/text:</P>
        <Script>We’re filming our Santa Barbara Open House Series this weekend — short cinematic reels featuring standout listings and the agents hosting them. Free, delivered next day. Yours at [address] caught our eye — OK if we swing by Sunday?</Script>
        <P>4. Pack: cards, a one-pager with a QR code to a 60-second sample reel, gimbal kit. No gifts — agents read gifts as bribes.</P>
      </Section>

      <Section title="Arrival — 20-minute hard cap per house">
        <P>Time it for lulls: the first ~20 minutes of the window (agent setting up, alone) or the last 30. Avoid peak mid-window.</P>
        <P>Both walk in dressed sharp, camera slung not raised. <strong>Sign in truthfully</strong> — name + Roto Flight Media. Never pose as buyers.</P>
        <P>First 10 seconds, talker leads:</P>
        <Script>Hi — I’m not a buyer, I’ll be quick. Cyprian, Roto Flight Media here in Santa Barbara. Gorgeous staging on this one.</Script>
        <P sx={{ color: 'error.main', fontWeight: 600 }}>The moment a buyer walks in: stop, step aside or leave. Never speak to buyers — it’s the fastest way to get blacklisted.</P>
      </Section>

      <Section title="The pitch — 2–3 minutes, lulls only">
        <P>Rapport first — one genuine question about the listing or their business. Then the offer:</P>
        <Script>We’re producing a short Open House Series this month — cinematic 45–60 second reels of a handful of standout SB listings, with the hosting agent on camera. No charge, you get it tomorrow, fully yours to post. Would you want this house in the series?</Script>
        <P>Then permission, explicitly — this builds trust because MLS rules make agents cautious about listing content:</P>
        <Script>It’s the seller’s home so it’s totally your call — we can stick to exteriors and details if interiors are off-limits, and nothing posts until you approve it.</Script>
        <P>
          <strong>If yes:</strong> Sierra shoots 10–15 min (exteriors, 5–6 hero interiors, one 15-second agent walk-and-talk). Cyprian gets the cell —{' '}
          <em>“What’s the best number to text the video to tomorrow?”</em> — plus the IG handle. That consent is what makes the follow-up text legitimate.
        </P>
        <P><strong>If hesitant:</strong> zero pressure.</P>
        <Script>If we feature other listings in the series, want me to text you the results so you can see what it looks like?</Script>
        <P>Card, exit. <strong>Roles:</strong> Cyprian talks, Sierra shoots — swap only if the agent clearly clicks better with Sierra.</P>
      </Section>

      <Section title="California legal — read this once, never forget it">
        <P>
          California is an <strong>all-party consent</strong> state. No ambient audio, ever. No filming buyers — faces or voices. Mic the agent
          only, on camera, with explicit consent. MLS rules make listing content the agent’s call: always ask, never post before they approve.
        </P>
      </Section>

      <Section title="Deliver & follow up — the follow-up is the product">
        <P>Same evening, send a text (Follow-up tab has the templates). Next day, <strong>before noon</strong>, deliver by text: one tracked link, vertical + horizontal cuts, downloadable, no watermark games.</P>
        <P>Two asks on delivery: permission to tag them when you share it, and — once they react positively — one Google review <em>or</em> one colleague intro.</P>
        <P>Then 8–12 light touches over ~3 weeks (text / IG / email, 50–125 words). Replies come in touches 3–7 — most vendors quit at 2. The tracked link tells you who’s watching.</P>
      </Section>

      <Section title="Convert & scale to the whole office">
        <P>Bridge line, ~1 week later:</P>
        <Script>When your next listing goes live we’ll do the full treatment — series alums get first-shoot pricing.</Script>
        <P>One free video per agent, <strong>ever</strong> — never discount the standing rate (low pricing attracts price-shoppers who don’t refer). After a delighted delivery:</P>
        <Script>Who runs your office sales meeting? We’d love to show the series in 3 minutes.</Script>
        <P>
          The path at Village: warm agent → office manager / marketing director → 3-minute sales-meeting demo (two finished SB reels, offer to the first
          2–3 agents with upcoming listings) → trusted-vendor list.
        </P>
      </Section>

      <Section title="Red lines">
        <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 0.5, color: 'text.secondary', fontSize: '0.85rem' } }}>
          <li>Talking to buyers, or filming them (faces or audio).</li>
          <li>Filming without the agent’s explicit OK, or posting before they approve.</li>
          <li>Fake sign-ins; staying past 20 minutes; showing up at peak and competing for attention.</li>
          <li>Trash-talking their current videographer — “we’d love to be your backup” wins.</li>
          <li>The donuts-and-flyers circuit; unsolicited spam texts (only text numbers given to you).</li>
        </Box>
      </Section>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Synthesized from agent forums, RE photographer/videographer marketing playbooks, and loan-officer open-house prospecting coaching.
        Tactics are guidance, not guarantees — read the room and protect the relationship first.
      </Typography>
    </Box>
  );
}
